import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react"
import history from "utils/history"
import { useLocation } from "react-router-dom"

import { UI_INITIAL_CONTEXT } from "providers/UIProvider/UIProviderConstants"
import { DISTRICTS_INITIAL_CONTEXT } from "./DistrictsProviderConstants"
import { IGetDistrictsData, IDistrict } from "types/district"
import { IDistrictsProvider, IDistrictsContext } from "./DistrictsProviderTypes"

import useGetDistricts from "api/querries/districts/useGetDistricts"
import useAddDistrictMutation from "api/mutations/districts/useAddDistrict"
import { useUI } from "providers/UIProvider"
import useDistricts from "hooks/useDistricts"
import useUpdateDistrictMutation from "api/mutations/districts/useUpdateDistrict"
import useDeleteDistrictMutation from "api/mutations/districts/useDeleteDistrict"

export const DistrictsContext = createContext<IDistrictsContext>(DISTRICTS_INITIAL_CONTEXT)

export const useDistrictsContext: () => IDistrictsContext = () => useContext(DistrictsContext)

const DistrictsProvider: React.FC<IDistrictsProvider> = ({ children }) => {
  const { search } = useLocation()

  const params = new URLSearchParams(search)
  const pageSize = Number(params.get("pageSize"))

  const { toggleModal } = useUI()
  const { form: { resetForm } } = useDistricts()

  const [districts, setDistricts] = useState<IGetDistrictsData>(({ itemCount: 0, skip: 0, take: 10, data: [] }))
  const [currentDistrict, setCurrentDistrict] = useState<IDistrict | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(pageSize || 1)

  const { data: districtsData, isFetched: districtsDataFetched, refetch: refetchDistricts } = useGetDistricts(
    { sortBy: 'name', pageIndex: currentPage, pageSize: districts.take },
    true
  )
  const { mutate: addDistrict, isLoading: addDistrictLoading, isSuccess: addDistrictSuccess } = useAddDistrictMutation()
  const { mutate: updateDistrict, isLoading: updateDistrictLoading, isSuccess: updateDistrictSuccess } = useUpdateDistrictMutation()
  const { mutate: deleteDistrict, isLoading: deleteDistrictLoading, isSuccess: deleteDistrictSuccess } = useDeleteDistrictMutation()

  const updateSearchParams = (params: any) => {
    history.push({
      search: `?${new URLSearchParams(params).toString()}`
    })
  }

  const handlePageChagne = (newPage: number) => {
    setCurrentPage(newPage)
    updateSearchParams({ pageSize: newPage })
  }

  useEffect(() => {
    if (districtsData && districtsDataFetched) {
      setDistricts(districtsData.data)
    }
  }, [districtsData, districtsDataFetched])

  useEffect(() => {
    refetchDistricts()
  }, [currentPage])


  useEffect(() => {
    if (addDistrictSuccess || updateDistrictSuccess) {
      refetchDistricts()
      toggleModal("addEditDistrictModal", false)
    }
  }, [addDistrictSuccess, updateDistrictSuccess])

  useEffect(() => {
    if (deleteDistrictSuccess) {
      refetchDistricts()
      toggleModal("confirmationModal", false, UI_INITIAL_CONTEXT.modals.confirmationModal.data)
    }
  }, [deleteDistrictSuccess])

  const value = useMemo(
    () => ({
      districts,
      addDistrict,
      addDistrictLoading,
      updateDistrict,
      updateDistrictLoading,
      deleteDistrict,
      deleteDistrictLoading,
      currentDistrict,
      setCurrentDistrict,
      handlePageChagne,
      currentPage
    }),
    [districts],
  )

  return (
    <DistrictsContext.Provider value={value}>
      {children}
    </DistrictsContext.Provider>
  )
}

export default DistrictsProvider